import { useEffect, useRef, useState, type ReactNode } from 'react'

import { Link, useNavigate, useRouterState } from '@tanstack/react-router'
import {
  AlertTriangle,
  Bell,
  BookOpen,
  Building2,
  FileText,
  LayoutDashboard,
  // LayoutGrid, // Dashboard nav disabled — see ADMIN_NAV below
  Lock,
  LogOut,
  MapPin,
  Menu,
  User as UserIcon,
  Users,
  X,
} from 'lucide-react'

import { cn } from '@/lib/utils'
import { useAuthStore } from '@/shared/auth/stores/authStore'
import { NotificationDropdown } from '@/shared/components/notifications/NotificationDropdown'
import { ConfirmDialog } from '@/shared/components/ui'
import { roleLabel } from '@/shared/constants/roles'
import { useUnreadNotificationCount } from '@/shared/hooks/useNotifications'
import { initialsOf } from '@/shared/utils/initials'

// ─── role-aware nav definitions ────────────────────────────────────────────

interface NavLinkDef {
  to: string
  label: string
  icon: ReactNode
}

const ADMIN_NAV: readonly NavLinkDef[] = [
  // Dashboard removed from the UI. The route is disabled (see
  // src/app/routes/dashboard.tsx.disabled). To restore: uncomment this entry,
  // re-enable the LayoutGrid import above, rename the route file back to
  // dashboard.tsx, and drop the tsconfig `exclude` for src/systems/dashboard.
  // { to: '/dashboard', label: 'Dashboard', icon: <LayoutGrid size={15} strokeWidth={2} /> },
  { to: '/gis', label: 'Dashboard', icon: <LayoutDashboard size={15} strokeWidth={2} /> },
  { to: '/entities', label: 'Entities', icon: <Building2 size={15} strokeWidth={2} /> },
  { to: '/user-management', label: 'User Management', icon: <Users size={15} strokeWidth={2} /> },
  { to: '/reports', label: 'Reports', icon: <FileText size={15} strokeWidth={2} /> },
]

const HOD_NAV: readonly NavLinkDef[] = [
  { to: '/hod/dashboard', label: 'My Dashboard', icon: <LayoutDashboard size={15} strokeWidth={2} /> },
  { to: '/hod/sites', label: 'My Sites', icon: <MapPin size={15} strokeWidth={2} /> },
  { to: '/hod/issues', label: 'My Issues', icon: <AlertTriangle size={15} strokeWidth={2} /> },
]

function navItemsForRole(role: string | undefined): readonly NavLinkDef[] {
  if (role === 'super_admin' || role === 'admin') {
    return ADMIN_NAV
  }
  if (role === 'hod') {
    return HOD_NAV
  }
  return []
}

// ─── nav link (active pill) ────────────────────────────────────────────────

function NavItem({ to, label, icon }: NavLinkDef) {
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const isActive = pathname === to || pathname.startsWith(`${to}/`)

  return (
    <Link
      to={to}
      className={cn(
        'inline-flex shrink-0 items-center gap-1.5 rounded-[10px] px-3.5 py-1.5 text-[13px] transition',
        isActive
          ? 'bg-ink text-white font-semibold shadow-[0_6px_14px_-6px_rgba(10,21,48,0.4)]'
          : 'text-ink-3 hover:text-ink hover:bg-ink/[0.04] font-medium',
      )}
    >
      {icon}
      {label}
    </Link>
  )
}

// ─── avatar bubble (initials, brand gradient) ──────────────────────────────

function AvatarBubble({ name, size = 34 }: { name: string | undefined; size?: number }) {
  return (
    <span
      style={{ width: size, height: size, fontSize: Math.round(size * 0.36) }}
      className="from-brand-2 to-brand inline-flex shrink-0 items-center justify-center rounded-full bg-gradient-to-br font-bold text-white"
    >
      {initialsOf(name)}
    </span>
  )
}

// ─── main nav ──────────────────────────────────────────────────────────────

export function AppNav() {
  const [isLogoutOpen, setIsLogoutOpen] = useState(false)
  const [isLoggingOut, setIsLoggingOut] = useState(false)
  const [isMobileOpen, setIsMobileOpen] = useState(false)
  const [isProfileOpen, setIsProfileOpen] = useState(false)
  const [isBellOpen, setIsBellOpen] = useState(false)

  const profileRef = useRef<HTMLDivElement>(null)
  const bellRef = useRef<HTMLButtonElement>(null)

  const { data: unreadData } = useUnreadNotificationCount()
  const unreadCount = unreadData?.unread_count ?? 0

  const navigate = useNavigate()
  const logout = useAuthStore((s) => s.logout)
  const user = useAuthStore((s) => s.user)
  const role = user?.role_name
  const navItems = navItemsForRole(role)

  useEffect(() => {
    function handleOutside(e: MouseEvent) {
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) {
        setIsProfileOpen(false)
      }
    }
    if (isProfileOpen) {
      document.addEventListener('mousedown', handleOutside)
    }
    return () => document.removeEventListener('mousedown', handleOutside)
  }, [isProfileOpen])

  async function handleLogout() {
    setIsLoggingOut(true)
    try {
      await logout()
      setIsLogoutOpen(false)
      navigate({ to: '/login' })
    } finally {
      setIsLoggingOut(false)
    }
  }

  return (
    <>
      {/* ─── Floating glass shell — fixed at the top.
              Inner wrapper shares max-w-[1240px] + px-6 with the page outlet
              so the navbar and content align edge-to-edge. ─── */}
      <div className="font-display fixed top-2.5 right-0 left-0 z-40">
        <div className="mx-auto w-full max-w-[1240px] px-6">
          <nav className="relative flex h-[48px] items-center gap-4 rounded-2xl border border-[rgba(11,21,48,0.06)] bg-white/78 px-2 pl-4 backdrop-blur-xl backdrop-saturate-150 shadow-[0_1px_0_rgba(255,255,255,0.85)_inset,0_18px_40px_-16px_rgba(11,21,48,0.18),0_6px_14px_-8px_rgba(11,21,48,0.10)]">
          {/* Brand */}
          <div className="flex shrink-0 items-center gap-2.5">
            <img
              src="/image/fimms_logo.png"
              alt="FIMMS"
              className="h-5 w-auto object-contain"
            />
          </div>

          {/* Center nav — absolute-centered so the brand on left + actions
              on right don't shift it. Hidden on small screens. */}
          <div className="absolute top-1/2 left-1/2 hidden -translate-x-1/2 -translate-y-1/2 items-center gap-0.5 md:flex">
            {navItems.map((item) => (
              <NavItem key={item.to} {...item} />
            ))}
          </div>

          {/* Right cluster */}
          <div className="ml-auto flex shrink-0 items-center gap-1.5">
            {/* Bell + dropdown */}
            <div className="relative">
              <button
                ref={bellRef}
                type="button"
                aria-label={
                  unreadCount > 0
                    ? `Notifications, ${unreadCount} unread`
                    : 'Notifications'
                }
                aria-expanded={isBellOpen}
                aria-haspopup="dialog"
                onClick={() => setIsBellOpen((o) => !o)}
                className={cn(
                  'relative grid size-8 place-items-center rounded-[10px] transition-colors',
                  isBellOpen
                    ? 'bg-brand-tint text-brand'
                    : 'text-ink-3 hover:bg-ink/[0.06] hover:text-ink',
                )}
              >
                <Bell size={17} strokeWidth={1.9} />
                {unreadCount > 0 && (
                  <span
                    className="absolute top-1.5 right-1.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[9px] font-bold text-white shadow-[0_0_0_2px_#fff]"
                    aria-hidden="true"
                  >
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>

              <NotificationDropdown
                open={isBellOpen}
                onClose={() => setIsBellOpen(false)}
                triggerRef={bellRef}
              />
            </div>

            {/* Profile menu */}
            <div ref={profileRef} className="relative ml-1">
              <button
                type="button"
                aria-label="Account menu"
                aria-expanded={isProfileOpen}
                aria-haspopup="menu"
                onClick={() => setIsProfileOpen((o) => !o)}
                className="rounded-full shadow-[0_0_0_2px_#fff,0_0_0_3.5px_rgba(11,59,145,0.18)] transition hover:shadow-[0_0_0_2px_#fff,0_0_0_3.5px_rgba(11,59,145,0.36)]"
              >
                <AvatarBubble name={user?.name} size={30} />
              </button>

              {isProfileOpen && (
                <div
                  role="menu"
                  className="border-line absolute top-full right-0 z-50 mt-2 w-72 overflow-hidden rounded-xl border bg-white shadow-[0_18px_40px_-16px_rgba(11,21,48,0.25)]"
                >
                  <div className="border-line flex items-center gap-3 border-b px-4 py-3">
                    <AvatarBubble name={user?.name} size={40} />
                    <div className="min-w-0 flex-1">
                      <p className="text-ink truncate text-sm font-bold">
                        {user?.name ?? 'Account'}
                      </p>
                      <p className="text-muted-text truncate text-[11px]">
                        {user?.email ?? ''}
                      </p>
                      <p className="bg-brand-tint text-brand mt-1 inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold tracking-wider uppercase">
                        {roleLabel(role)}
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-col py-1">
                    <Link
                      to="/profile"
                      onClick={() => setIsProfileOpen(false)}
                      role="menuitem"
                      className="text-ink-2 hover:bg-line-2 flex items-center gap-2 px-4 py-2 text-sm transition-colors"
                    >
                      <UserIcon size={14} className="text-muted-text" />
                      View profile
                    </Link>
                    <Link
                      to="/profile"
                      search={{ action: 'change-password' }}
                      onClick={() => setIsProfileOpen(false)}
                      role="menuitem"
                      className="text-ink-2 hover:bg-line-2 flex items-center gap-2 px-4 py-2 text-sm transition-colors"
                    >
                      <Lock size={14} className="text-muted-text" />
                      Change password
                    </Link>
                    <Link
                      to="/manual"
                      onClick={() => setIsProfileOpen(false)}
                      role="menuitem"
                      className="text-ink-2 hover:bg-line-2 flex items-center gap-2 px-4 py-2 text-sm transition-colors"
                    >
                      <BookOpen size={14} className="text-muted-text" />
                      User manual
                    </Link>
                  </div>

                  <div className="border-line border-t">
                    <button
                      type="button"
                      onClick={() => {
                        setIsProfileOpen(false)
                        setIsLogoutOpen(true)
                      }}
                      role="menuitem"
                      className="text-danger hover:bg-danger-soft flex w-full items-center gap-2 px-4 py-2 text-sm font-semibold transition-colors"
                    >
                      <LogOut size={14} />
                      Sign out
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Mobile hamburger */}
            <button
              type="button"
              aria-label="Toggle menu"
              className="text-ink-3 hover:bg-ink/[0.06] hover:text-ink grid size-9 place-items-center rounded-[10px] md:hidden"
              onClick={() => setIsMobileOpen((o) => !o)}
            >
              {isMobileOpen ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
          </nav>

          {/* Mobile drawer — opens beneath the glass nav, shares the same
              max-w / px-6 frame so it aligns with the nav above it. */}
          {isMobileOpen && (
            <div className="border-line mt-2 rounded-2xl border bg-white/95 p-2 shadow-[0_18px_40px_-16px_rgba(11,21,48,0.18)] backdrop-blur md:hidden">
              <div className="flex flex-col gap-1">
                {navItems.map((item) => (
                  <NavItem key={item.to} {...item} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={isLogoutOpen}
        onClose={() => setIsLogoutOpen(false)}
        onConfirm={handleLogout}
        title="Log out"
        description="Are you sure you want to log out of your account?"
        confirmLabel="Yes, log out"
        variant="danger"
        isPending={isLoggingOut}
      />
    </>
  )
}
